import type { ContextMenuEntry, ContextMenuItem } from './contextMenuConfig';
import { useSketchStore } from '../state/useSketchStore';
import { parseFaceSelectionId, parseEdgeSelectionId } from '../core/sketch/types';

/**
 * Sketch workbench context menu — the SketchUp-style right-click menu.
 *
 * Built at right-click time from the live sketch state: the picked entity
 * kind (face composite / whole solid / empty space) decides which sections
 * appear, mirroring SketchUp's entity-sensitive menu. Action ids reference
 * the commands registered from tools/sketch/sketchContextActions.ts, so the
 * tree and the command surface can never drift apart.
 */

const item = (entry: ContextMenuItem): ContextMenuItem => entry;
const divider = (): ContextMenuEntry => ({ type: 'divider' as const });

/** True when any hidden (solid-level) geometry exists — drives Unhide. */
function hasHiddenSolids(): boolean {
    return Object.values(useSketchStore.getState().solids).some((solid) => !solid.visible);
}

/** Select ▸ submenu (entity menu and empty-space menu share it). */
function selectSubmenu(withMaterialEntry: boolean): ContextMenuEntry[] {
    const entries: ContextMenuEntry[] = [
        item({ id: 'sketchCtxSelectAll', label: 'All', action: 'sketchCtxSelectAll' }),
        item({ id: 'sketchCtxSelectNone', label: 'None', action: 'sketchCtxSelectNone' }),
        item({ id: 'sketchCtxSelectInverse', label: 'Inverse', action: 'sketchCtxSelectInverse' }),
    ];
    if (withMaterialEntry) {
        entries.push(divider());
        entries.push(item({
            id: 'sketchCtxSelectSameMaterial',
            label: 'All with Same Material',
            action: 'sketchCtxSelectSameMaterial',
        }));
    }
    return entries;
}

/** Area ▸ submenu (SketchUp reports selection and model totals). */
function areaSubmenu(): ContextMenuEntry[] {
    return [
        item({ id: 'sketchCtxAreaSelection', label: 'Selection', action: 'sketchCtxAreaSelection' }),
        item({ id: 'sketchCtxAreaFaces', label: 'Faces', action: 'sketchCtxAreaFaces' }),
    ];
}

/** Intersect Faces ▸ submenu (face selections only, like SketchUp). */
function intersectSubmenu(): ContextMenuEntry[] {
    return [
        item({ id: 'sketchCtxIntersectModel', label: 'With Model', action: 'sketchCtxIntersectModel' }),
        item({ id: 'sketchCtxIntersectContext', label: 'With Context', action: 'sketchCtxIntersectContext' }),
    ];
}

/** Flip Along ▸ (Red/Green/Blue — Sketch's axis colors). */
function flipSubmenu(): ContextMenuEntry[] {
    return [
        item({ id: 'sketchCtxFlipX', label: 'Red (X)', action: 'sketchCtxFlipX' }),
        item({ id: 'sketchCtxFlipY', label: 'Green (Y)', action: 'sketchCtxFlipY' }),
        item({ id: 'sketchCtxFlipZ', label: 'Blue (Z)', action: 'sketchCtxFlipZ' }),
    ];
}

/** Unhide ▸ (empty-space menu; Last only when a Hide happened this session). */
function unhideSubmenu(): ContextMenuEntry[] {
    const entries: ContextMenuEntry[] = [
        item({ id: 'sketchCtxUnhideAll', label: 'All', action: 'sketchCtxUnhideAll' }),
    ];
    entries.push(item({ id: 'sketchCtxUnhideLast', label: 'Last', action: 'sketchCtxUnhideLast' }));
    return entries;
}

/** Menu over empty space: selection scope + unhide + navigation. */
function emptySpaceMenu(): ContextMenuEntry[] {
    const hasSelection = useSketchStore.getState().selectedIds.length > 0;
    const entries: ContextMenuEntry[] = [];

    // SketchUp's empty-space menu over an active selection: Invert Selection
    // plus Make Component/Group acting on the CURRENT selection (right-click
    // on empty space never clears it). Without a selection only the scope
    // entries make sense.
    entries.push({
        id: 'sketchSelectSubmenu',
        label: 'Select',
        submenu: selectSubmenu(false),
    });
    if (hasSelection) {
        entries.push(divider());
        entries.push(item({
            id: 'sketchCtxSelectInverse',
            label: 'Invert Selection',
            action: 'sketchCtxSelectInverse',
        }));
        entries.push(divider());
        entries.push(item({
            id: 'sketchCtxMakeComponent',
            label: 'Make Component...',
            action: 'sketchCtxMakeComponent',
        }));
        entries.push(item({
            id: 'sketchCtxMakeGroup',
            label: 'Make Group',
            action: 'sketchCtxMakeGroup',
        }));
    }
    entries.push(divider());
    entries.push({
        id: 'sketchCtxUnhide',
        label: 'Unhide',
        submenu: unhideSubmenu(),
    });
    if (useSketchStore.getState().sectionBox) {
        entries.push(item({
            id: 'sketchCtxRemoveClippingBox',
            label: 'Remove Clipping Box',
            action: 'sketchCtxRemoveClippingBox',
        }));
    }
    entries.push(divider());
    entries.push(item({ id: 'sketchZoomExtents', label: 'Zoom Extents', action: 'sketchZoomExtents', shortcut: 'Shift+Z' }));
    return entries;
}

/** Menu over a sketch entity: the full SketchUp-style entity menu. */
function entityMenu(): ContextMenuEntry[] {
    const sketch = useSketchStore.getState();
    const hasFace = sketch.selectedIds.some((id) => {
        const parsed = parseFaceSelectionId(id);
        return Boolean(parsed && sketch.solids[parsed.solidId]?.faces.some((f) => f.id === parsed.faceId));
    });
    const hasWholeSolid = sketch.selectedIds.some((id) => Boolean(sketch.solids[id]));
    const ownerSolidIds = new Set<string>();
    for (const id of sketch.selectedIds) {
        const parsedFace = parseFaceSelectionId(id);
        if (parsedFace && sketch.solids[parsedFace.solidId]) ownerSolidIds.add(parsedFace.solidId);
        else {
            const parsedEdge = parseEdgeSelectionId(id);
            if (parsedEdge && sketch.solids[parsedEdge.solidId]) ownerSolidIds.add(parsedEdge.solidId);
            else if (sketch.solids[id]) ownerSolidIds.add(id);
        }
    }
    const anyLocked = Array.from(ownerSolidIds).some((id) => sketch.solids[id]?.locked);
    const anyGrouped = Array.from(ownerSolidIds).some((id) => sketch.solids[id]?.groupId);

    const entries: ContextMenuEntry[] = [
        item({ id: 'sketchCtxEntityInfo', label: 'Entity Info', action: 'sketchCtxEntityInfo' }),
        divider(),
        item({ id: 'sketchCtxErase', label: 'Erase', action: 'sketchCtxErase', shortcut: 'Del' }),
        item({ id: 'sketchCtxHide', label: 'Hide', action: 'sketchCtxHide' }),
        divider(),
        {
            id: 'sketchSelectSubmenu',
            label: 'Select',
            submenu: selectSubmenu(true),
        },
        {
            id: 'sketchAreaSubmenu',
            label: 'Area',
            submenu: areaSubmenu(),
        },
    ];

    if (hasFace) {
        entries.push({
            id: 'sketchIntersectSubmenu',
            label: 'Intersect Faces',
            submenu: intersectSubmenu(),
        });
        entries.push(divider());
        entries.push(item({ id: 'sketchCtxAlignView', label: 'Align View', action: 'sketchCtxAlignView' }));
        entries.push(item({ id: 'sketchCtxAlignAxes', label: 'Align Axes', action: 'sketchCtxAlignAxes' }));
    }

    entries.push(
        item({ id: 'sketchCtxReverseFaces', label: 'Reverse Faces', action: 'sketchCtxReverseFaces' }),
        item({ id: 'sketchCtxOrientFaces', label: 'Orient Faces', action: 'sketchCtxOrientFaces' }),
    );

    if (hasWholeSolid) {
        entries.push(divider());
        entries.push({
            id: 'sketchFlipSubmenu',
            label: 'Flip Along',
            submenu: flipSubmenu(),
        });
    }

    entries.push(divider());
    entries.push(item({
        id: 'sketchCtxLockToggle',
        label: anyLocked ? 'Unlock' : 'Lock',
        action: anyLocked ? 'sketchCtxUnlock' : 'sketchCtxLock',
    }));
    if (hasWholeSolid) {
        entries.push(item({ id: 'sketchCtxMakeGroup', label: 'Make Group', action: 'sketchCtxMakeGroup' }));
        entries.push(item({ id: 'sketchCtxMakeComponent', label: 'Make Component', action: 'sketchCtxMakeComponent' }));
    }
    if (anyGrouped) {
        entries.push(item({ id: 'sketchCtxUngroup', label: 'Ungroup', action: 'sketchCtxUngroup' }));
    }

    entries.push(divider());
    entries.push(item({ id: 'sketchCtxZoomSelection', label: 'Zoom Selection', action: 'sketchCtxZoomSelection' }));
    return entries;
}

/**
 * Build the Sketch context menu for the CURRENT selection: an entity menu
 * over picked geometry, the empty-space menu otherwise. (Foreign BIM/CAD
 * references keep the standard workbench menus — the SketchViewport routes
 * to this builder only when sketch geometry owns the selection.)
 */
export function buildSketchContextMenu(): ContextMenuEntry[] {
    const sketch = useSketchStore.getState();
    if (sketch.selectedIds.length === 0) return emptySpaceMenu();
    return entityMenu();
}
